'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Bell, CheckCheck, Trash2, AlertTriangle, CheckCircle2, Info, TrendingUp, Zap } from 'lucide-react';

interface Notification {
  id: string;
  type: 'alert' | 'success' | 'info' | 'trend';
  title: string;
  body: string;
  time: string;
  read: boolean;
}

const initialNotifications: Notification[] = [
  { id: '1', type: 'alert', title: 'Publish rate limit hit', body: 'Meta Graph API returned 429 — next publish window opens in 14 minutes. 2 reels queued.', time: '8 min ago', read: false },
  { id: '2', type: 'trend', title: 'High-velocity signal detected', body: '"AI productivity tools" reached velocity score 94/100 — script generation triggered automatically.', time: '12 min ago', read: false },
  { id: '3', type: 'success', title: 'Reel published', body: '"5 AI Tools That Replace Your Entire Team" is live on Instagram. Early engagement: 847 views in 10 min.', time: '18 min ago', read: false },
  { id: '4', type: 'info', title: 'Render queue at 78% capacity', body: '7 of 9 render slots occupied. Consider scaling render workers to avoid pipeline delays.', time: '25 min ago', read: false },
  { id: '5', type: 'success', title: 'DM funnel triggered × 3', body: 'Keywords "link", "info", "how" matched — 3 automated DM replies sent to engaged followers.', time: '32 min ago', read: true },
  { id: '6', type: 'success', title: 'Reel published', body: '"ChatGPT Prompt Engineering Secrets" is live. 1.2K views in first 30 minutes.', time: '1 hr ago', read: true },
  { id: '7', type: 'info', title: 'Weekly pipeline summary', body: '14 reels published this week. Avg views: 3,240. Top performer: "Make $500/Day with AI" — 12.4K views.', time: '3 hr ago', read: true },
  { id: '8', type: 'alert', title: 'Render worker timeout', body: 'Worker #3 timed out after 120s on job #88. Job was automatically requeued and completed successfully.', time: '5 hr ago', read: true },
  { id: '9', type: 'trend', title: 'Trending topic: "Passive income AI"', body: 'Signal velocity 87/100 — 3 competing creators posted on this topic in the last 2 hours. Act fast.', time: '6 hr ago', read: true },
  { id: '10', type: 'success', title: 'Monetization milestone', body: 'Instagram account crossed 10K followers — DM funnel conversion rate: 8.4% this week.', time: '1 day ago', read: true },
];

const typeConfig = {
  alert: { icon: AlertTriangle, color: 'text-amber-400', dot: 'bg-amber-400', bg: 'bg-amber-400/8' },
  success: { icon: CheckCircle2, color: 'text-emerald-400', dot: 'bg-emerald-400', bg: 'bg-emerald-400/8' },
  info: { icon: Info, color: 'text-sky-400', dot: 'bg-sky-400', bg: 'bg-sky-400/8' },
  trend: { icon: TrendingUp, color: 'text-violet-400', dot: 'bg-violet-400', bg: 'bg-violet-400/8' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState(initialNotifications);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');

  const unreadCount = notifications.filter((n) => !n.read).length;
  const displayed = activeTab === 'unread' ? notifications.filter((n) => !n.read) : notifications;

  const markAllRead = () => setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  const markRead = (id: string) => setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  const dismiss = (id: string) => setNotifications((prev) => prev.filter((n) => n.id !== id));

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Bell size={18} className="text-primary" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-negative text-white text-2xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Notifications</h1>
              <p className="text-xs text-muted-foreground font-mono">{unreadCount} unread · {notifications.length} total</p>
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <CheckCheck size={13} />
              Mark all read
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 rounded-lg bg-secondary border border-border w-fit">
          {(['all', 'unread'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors capitalize ${
                activeTab === tab
                  ? 'bg-card text-foreground shadow-sm border border-border'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
              {tab === 'unread' && unreadCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-negative text-white text-2xs font-semibold">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Notification List */}
        <div className="space-y-2">
          {displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 rounded-xl border border-border bg-card">
              <Zap size={32} className="text-muted-foreground mb-3 opacity-40" />
              <p className="text-sm text-muted-foreground">No unread notifications</p>
            </div>
          ) : (
            displayed.map((notif) => {
              const cfg = typeConfig[notif.type];
              const NotifIcon = cfg.icon;
              return (
                <div
                  key={notif.id}
                  className={`group relative flex gap-4 px-4 py-4 rounded-xl border transition-all ${
                    !notif.read
                      ? `${cfg.bg} border-border`
                      : 'bg-card border-border opacity-70 hover:opacity-100'
                  }`}
                >
                  {!notif.read && (
                    <span className={`absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  )}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg} border border-border`}>
                    <NotifIcon size={15} className={cfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-medium ${notif.read ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {notif.title}
                      </p>
                      <span className="text-xs text-muted-foreground font-mono shrink-0">{notif.time}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{notif.body}</p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {!notif.read && (
                      <button
                        onClick={() => markRead(notif.id)}
                        title="Mark as read"
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <CheckCheck size={13} />
                      </button>
                    )}
                    <button
                      onClick={() => dismiss(notif.id)}
                      title="Dismiss"
                      className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </AppLayout>
  );
}
